/**
 * The `@` menu's popup list — the old web's `MentionList.tsx`. One flat list:
 * a grey badge per type key, or a `type: name` row per item / search hit.
 * Arrow keys wrap, Enter *and* Tab select, and the selected row scrolls into
 * view; the selection resets whenever the items change.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { MentionOption } from "../../lib/mentionOptions";

export interface MentionListProps {
  items: MentionOption[];
  command: (item: MentionOption) => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(function MentionList(props, ref) {
  const { t } = useLocale();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) props.command(item);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useEffect(() => {
    const row = listRef.current?.children[selectedIndex] as HTMLElement | undefined;
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      const count = props.items.length;
      if (event.key === "ArrowUp") {
        if (count > 0) setSelectedIndex((selectedIndex + count - 1) % count);
        return true;
      }
      if (event.key === "ArrowDown") {
        if (count > 0) setSelectedIndex((selectedIndex + 1) % count);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="mention-list">
      {props.items.length === 0 ? (
        <div className="mention-list-empty">{t("mention.noResults")}</div>
      ) : (
        <div ref={listRef} className="mention-list-items">
          {props.items.map((item, index) => (
            <button
              key={`${item.type}-${item.value}-${index}`}
              type="button"
              className={`mention-row${index === selectedIndex ? " is-selected" : ""}`}
              onClick={() => selectItem(index)}
            >
              {item.type === "key" ? (
                <span className="mention-row-key">{item.label}</span>
              ) : (
                <>
                  <span className="mention-row-type">{item.typeDisplayName ?? item.typeName}:</span>
                  <span className="mention-row-main">
                    <span className="mention-row-label">{item.label}</span>
                    {item.secondaryLabel ? <span className="mention-row-secondary">{item.secondaryLabel}</span> : null}
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
